import React from 'react';
import {
    Grid,
    Button,
    Divider,
    IconButton
} from '@mui/material';
import {
    CardMembership,
    CheckBox,
    CheckBoxOutlineBlank,
    Add,
    Remove
} from '@mui/icons-material';
import { BoolField, TextField } from '../../CommonComponents/Form';
import { SectionDivider } from '../../CommonComponents';
import METADATA_TYPES from '../../../types/MetadataType';
import {
    hasStandardProperty,
    isInputAlreadyInList,
    isCustomCertInPresets,
    changeStandardProperty
} from '../../../utils/metadata';

type CustomLicenceType = { id: string; value: string };

interface LicencesCertificatesBlockProps {
    settables: string[];
    entity: any;
    onChange: (e: any) => void;
    customLicCerts: CustomLicenceType[];
    setterCustomLicCerts: (items: CustomLicenceType[]) => void;
}

const LicencesCertificatesBlock = ({
    settables,
    entity,
    onChange,
    customLicCerts,
    setterCustomLicCerts
}: LicencesCertificatesBlockProps) => {
    const [selected, setSelected] = React.useState<'all' | 'some' | 'none'>('some');
    const entityCustoms = entity.customLicencesCertificates || [];
    const localCustoms = customLicCerts || [];
    const localStandards = entity.licencesCertificates;

    const licenseCount = (entity.licencesCertificates?.filter((lic: string) => settables.includes(lic)).length || 0)
        + localCustoms.filter((clic) => clic.value.trim()).length;

    const selectAll = () => {
        const newCerts = Array.from(new Set([...localStandards, ...settables]));
        onChange({ ...entity, licencesCertificates: newCerts });
    };

    const selectNone = () => {
        const currentCerts = entity.licencesCertificates || [];
        const newCerts = currentCerts.filter((cert: string) => !settables.includes(cert));
        onChange({ ...entity, licencesCertificates: newCerts });
    };

    const addCustomLicCert: () => void = () => (
        setterCustomLicCerts([...localCustoms, { id: `custom-cert-${Date.now().toString()}`, value: '' }])
    );

    const changeCustomLicCert = (
        { key, id, newValue }
            : { key: string; id: string; newValue: string; }
    ) => {
        const item = localCustoms.find((i: CustomLicenceType) => i.id === id);

        const oldValue = (item as any)?.value;

        // eslint-disable-next-line no-confusing-arrow
        const newItems = localCustoms.map((i: CustomLicenceType) => i.id === id
            ? { ...i, value: newValue }
            : i);

        setterCustomLicCerts(newItems);

        const updatedValues = [...(entityCustoms || [])];

        if (oldValue) {
            const oldIndex = updatedValues.indexOf(oldValue);
            if (oldIndex > -1) {
                updatedValues.splice(oldIndex, 1);
            }
        }

        if (newValue && !updatedValues.includes(newValue)) {
            updatedValues.push(newValue);
        }

        onChange({
            ...entity,
            [key]: updatedValues
        });
    };

    const removeCustomLicCert = (
        { key, id }
            : { key: string; id: string }
    ) => {
        const item = localCustoms.find((c: CustomLicenceType) => c.id === id);
        const newItems = localCustoms.filter((c: CustomLicenceType) => c.id !== id);

        setterCustomLicCerts(newItems);

        if ((item as any)?.value && entityCustoms?.includes((item as any).value)) {
            onChange({
                ...entity,
                [key]: entityCustoms.filter((p: string) => p !== (item as any).value)
            });
        }
    };

    React.useEffect(() => {
        if (settables.every((cert) => entity.licencesCertificates?.includes(cert))) {
            setSelected('all');
        } else if (!settables.some((cert) => entity.licencesCertificates?.includes(cert))) {
            setSelected('none');
        } else {
            setSelected('some');
        }
    }, [entity, settables]);

    return (
        <Grid item lg={12} xl={6}>
            <SectionDivider
                label={`Licenser och certifikat  (${licenseCount})`}
                icon={<CardMembership fontSize="small" />}
                marginTop={2}
                marginBottom={0}
            />
            <Grid container spacing={1} sx={{ mb: 1 }}>
                <Grid item>
                    <Button
                        size="small"
                        variant="text"
                        onClick={() => selectAll()}
                        startIcon={selected === 'all' ? <CheckBox /> : <CheckBoxOutlineBlank />}
                        sx={{ fontWeight: 300 }}
                    >
                        Alla
                    </Button>
                </Grid>
                <Grid item>
                    <Button
                        size="small"
                        variant="text"
                        onClick={() => selectNone()}
                        startIcon={selected === 'none' ? <CheckBox /> : <CheckBoxOutlineBlank />}
                        sx={{ fontWeight: 300 }}
                    >
                        Inga
                    </Button>
                </Grid>
            </Grid>
            <Divider sx={{ mb: 2, width: '160px' }} />
            <Grid container spacing={1}>
                {settables.map((cert: string) => (
                    <Grid item xs={4} key={cert}>
                        <BoolField
                            name={cert}
                            label={cert}
                            value={hasStandardProperty({ array: entity.licencesCertificates, value: cert })}
                            onChange={() => changeStandardProperty({
                                key: METADATA_TYPES.LICENCES_CERTIFICATES,
                                array: entity.licencesCertificates,
                                value: cert,
                                entity,
                                onChange
                            })}
                        />
                    </Grid>
                ))}
                {customLicCerts.map((cert) => (
                    <Grid item xs={12} key={cert.id} container spacing={1} alignItems="center">
                        <Grid item xs>
                            <TextField
                                name={`custom-cert-${cert.id}`}
                                label="Egen licens/certifikat"
                                value={cert.value}
                                onChange={(e) => {
                                    const newValue = e.target ? e.target.value : (e[`custom-cert-${cert.id}`] ?? '');
                                    changeCustomLicCert({
                                        key: METADATA_TYPES.CUSTOM_LICENCES_CERTIFICATES,
                                        id: cert.id,
                                        newValue
                                    });
                                }}
                                required
                                customValidations={[
                                    {
                                        isValid: !isInputAlreadyInList({
                                            id: cert.id,
                                            value: cert.value,
                                            list: customLicCerts
                                        }),
                                        errorText: 'Detta certifikat finns redan i listan'
                                    },
                                    {
                                        isValid: !isCustomCertInPresets({
                                            value: cert.value,
                                            presets: settables
                                        }),
                                        errorText: 'Detta certifikat finns som förinställt alternativ.'
                                    }
                                ]}
                                disabled={cert.id.startsWith('init-')}
                            />
                        </Grid>
                        <Grid item>
                            <IconButton
                                onClick={() => removeCustomLicCert({
                                    key: METADATA_TYPES.CUSTOM_LICENCES_CERTIFICATES,
                                    id: cert.id
                                })}
                                color="error"
                                size="small"
                            >
                                <Remove />
                            </IconButton>
                        </Grid>
                    </Grid>
                ))}
                <Grid item xs={12}>
                    <IconButton
                        onClick={() => addCustomLicCert()}
                        color="primary"
                        size="small"
                    >
                        <Add />
                    </IconButton>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default React.memo(LicencesCertificatesBlock);
